import React from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import { Box, MenuItem, Stack } from '@mui/material';
import { useTranslate } from 'react-admin';
import { sanitizeHtml } from '../sanitizeHtml';
import { CALLOUT_VARIANT_KEYS, getCalloutVariant } from '../calloutVariants';
import { AttrTextField, stopEditorEvents } from './AttrFields';

/**
 * Editor node view of the callout (Hinweisbox) block. The text is edited as
 * HTML in a multiline field while the box shows the sanitised result.
 */
export const CalloutBlockView: React.FC<NodeViewProps> = ({ node, selected, updateAttributes }) => {
    const translate = useTranslate();
    const { variant, html } = node.attrs as { variant: string; html: string };
    const style = getCalloutVariant(variant);
    const Icon = style.icon;

    return (
        <NodeViewWrapper className={`oa-block oa-block-callout${selected ? ' is-selected' : ''}`} data-type="callout-block" data-variant={variant}>
            <Box
                sx={{
                    display: 'flex',
                    gap: 1.5,
                    p: 2,
                    borderRadius: 'var(--oa-radius-md, 8px)',
                    border: `1px solid ${style.border}`,
                    backgroundColor: style.background,
                    color: style.color,
                }}
            >
                <Icon sx={{ color: style.iconColor, mt: 0.25 }} />
                <Box
                    className="oa-richtext"
                    sx={{ flex: 1, '& p': { m: 0, mb: 0.5 }, '& p:last-child': { mb: 0 } }}
                    dangerouslySetInnerHTML={{ __html: html ? sanitizeHtml(html) : `<p>${translate('blockEditor.callout.placeholder')}</p>` }}
                />
            </Box>

            {selected && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }} {...stopEditorEvents}>
                    <AttrTextField
                        size="small"
                        select
                        label={translate('blockEditor.callout.variant')}
                        value={variant || 'info'}
                        onValueChange={(v) => updateAttributes({ variant: v })}
                        sx={{ minWidth: 160 }}
                    >
                        {CALLOUT_VARIANT_KEYS.map((key) => (
                            <MenuItem key={key} value={key}>{translate(`blockEditor.callout.variants.${key}`)}</MenuItem>
                        ))}
                    </AttrTextField>
                    <AttrTextField
                        size="small"
                        multiline
                        minRows={2}
                        label={translate('blockEditor.callout.text')}
                        helperText={translate('blockEditor.callout.htmlHint')}
                        value={html}
                        onValueChange={(v) => updateAttributes({ html: v })}
                        fullWidth
                    />
                </Stack>
            )}
        </NodeViewWrapper>
    );
};
